import type { UndoCommand, CommandContext } from "./types";
import type { LineStringFeature } from "../../../../utils/models/LineStringFeature";

export class UndoManager {
  private undoStack: UndoCommand[] = [];
  private redoStack: UndoCommand[] = [];
  private maxSize: number = 80;

  /** Push a new command onto the undo stack. Clears redo. */
  push(command: UndoCommand): void {
    this.undoStack.push(command);
    this.redoStack = [];

    if (this.undoStack.length > this.maxSize) {
      this.undoStack.shift();
    }
  }

  /** Undo the last command. Returns true if an undo was performed. */
  undo(ctx: CommandContext): boolean {
    const command = this.undoStack.pop();
    if (!command) return false;

    command.undo(ctx);
    this.redoStack.push(command);
    return true;
  }

  /** Redo the last undone command. Returns true if a redo was performed. */
  redo(ctx: CommandContext): boolean {
    const command = this.redoStack.pop();
    if (!command) return false;

    command.redo(ctx);
    this.undoStack.push(command);
    return true;
  }

  /**
   * Called when a provisional line is replaced with the real snapped route.
   * Searches backward through the undo stack for a command that references
   * the provisional line ID and updates it with the real data.
   */
  updateProvisionalLine(provisionalId: string, realLine: LineStringFeature): void {
    for (let i = this.undoStack.length - 1; i >= 0; i--) {
      const cmd = this.undoStack[i];
      if (cmd.updateProvisionalLine) {
        if (cmd.updateProvisionalLine(provisionalId, realLine)) {
          return;
        }
      }
    }
  }

  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  /** Clear both stacks (e.g., on clearMap) */
  clear(): void {
    this.undoStack = [];
    this.redoStack = [];
  }
}
